import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { trackEvent } from './lib/analytics'
import { reportError } from './lib/reportError'

const MAX_MESSAGE_LEN = 2000

function FeedbackForm() {
  const [form, setForm] = useState({ message: '', email: '' })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === 'message') {
      // Extra safety: cap in JS even though maxLength exists
      const next = value.length > MAX_MESSAGE_LEN ? value.slice(0, MAX_MESSAGE_LEN) : value
      setForm((prev) => ({ ...prev, message: next }))
      return
    }

    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMsg('')

    const message = form.message.trim()
    const email = form.email.trim()

    if (!message) return
    if (message.length > MAX_MESSAGE_LEN) {
      setErrorMsg(`Το μήνυμα δεν μπορεί να ξεπερνά τους ${MAX_MESSAGE_LEN} χαρακτήρες.`)
      return
    }

    setLoading(true)
    const { error } = await supabase.from('feedback').insert([
      {
        source: 'about_page',
        message,
        email: email || null,
      },
    ])
    setLoading(false)

    if (error) {
      console.error('Feedback submission error:', error)
      reportError(error, {
        where: 'feedback_submit',
        source: 'about_page',
        has_email: !!email,
        message_len: message.length,
      })

      // Friendly message for constraint violation
      if (String(error.message || '').toLowerCase().includes('feedback_message_len')) {
        setErrorMsg(`Το μήνυμα δεν μπορεί να ξεπερνά τους ${MAX_MESSAGE_LEN} χαρακτήρες.`)
      } else {
        setErrorMsg('Κάτι πήγε στραβά. Δοκίμασε ξανά σε λίγο.')
      }
      return
    }

    trackEvent('feedback_submitted', {
      source: 'about_page',
      has_email: !!email,
      message_len: message.length,
    })
    
    setSubmitted(true)
    setForm({ message: '', email: '' })
  }

  if (submitted) {
    return (
      <div className="mt-10 p-5 md:p-6 bg-green-50 border border-green-200 rounded-xl">
        <p className="text-green-700 font-semibold">Ευχαριστούμε για τα σχόλιά σου!</p>
        <p className="text-green-700/80 text-sm mt-1">
          Τα σχόλια αποθηκεύτηκαν και θα τα λάβουμε υπόψη μας.
        </p>
      </div>
    )
  }

  const remaining = MAX_MESSAGE_LEN - form.message.length
  const atLimit = remaining === 0

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-10 md:mt-12 bg-white border border-gray-100 rounded-xl shadow-md p-5 md:p-6"
    >
      <h2 className="text-xl md:text-2xl font-semibold mb-2 text-gray-700">Έχεις προτάσεις;</h2>
      <p className="mb-4 text-gray-600">Βοήθησε μας να βελτιώσουμε την πλατφόρμα με τα σχόλιά σου.</p>

      <div className="mb-4">
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email (προαιρετικό)
        </label>
        <input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="name@example.com"
          value={form.email}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base h-11 focus:outline-none focus:ring-2 focus:ring-gray-300"
        />
      </div>

      <div className="flex items-center justify-between mb-1">
        <label htmlFor="message" className="text-sm font-medium text-gray-700">
          Μήνυμα
        </label>
        <span className={`text-xs ${atLimit ? 'text-red-600' : 'text-gray-500'}`}>
          {form.message.length}/{MAX_MESSAGE_LEN}
        </span>
      </div>

      <textarea
        id="message"
        name="message"
        required
        value={form.message}
        onChange={handleChange}
        rows={4}
        maxLength={MAX_MESSAGE_LEN}
        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base h-auto focus:outline-none focus:ring-2 focus:ring-gray-300"
        placeholder="Γράψε εδώ τις προτάσεις σου..."
      />

      {atLimit && (
        <p className="mt-2 text-sm text-red-600">
          Έφτασες το όριο των {MAX_MESSAGE_LEN} χαρακτήρων.
        </p>
      )}

      {errorMsg && (
        <p className="mt-3 text-sm text-red-600">{errorMsg}</p>
      )}

      <div className="mt-4">
        <button
          type="submit"
          disabled={loading}
          className="bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white font-medium px-5 py-3 rounded-lg transition cursor-pointer w-full sm:w-auto"
        >
          {loading ? 'Υποβολή…' : 'Υποβολή'}
        </button>
      </div>
    </form>
  )
}

export default FeedbackForm
